/**
 * Minimal markdown renderer: bold (**…**), italic (*…*), links ([text](url)).
 * Returns an array of DOM nodes — never sets innerHTML.
 * All other input is HTML-escaped and emitted as text.
 */

function escapeHtml(raw: string): string {
  return raw
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

type Token =
  | { type: 'text'; raw: string }
  | { type: 'bold'; inner: string }
  | { type: 'italic'; inner: string }
  | { type: 'link'; text: string; url: string };

function tokenize(input: string): Token[] {
  const tokens: Token[] = [];
  // Match bold, italic, and links in order of appearance.
  // Bold before italic so **bold *italic* in bold** parses outer bold first.
  const pattern = /\*\*(.+?)\*\*|\[([^\]]+)\]\(([^)]+)\)|\*([^*]+)\*/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = pattern.exec(input)) !== null) {
    if (match.index > lastIndex) {
      tokens.push({ type: 'text', raw: input.slice(lastIndex, match.index) });
    }

    if (match[1] !== undefined) {
      tokens.push({ type: 'bold', inner: match[1] });
    } else if (match[2] !== undefined && match[3] !== undefined) {
      tokens.push({ type: 'link', text: match[2], url: match[3] });
    } else if (match[4] !== undefined) {
      tokens.push({ type: 'italic', inner: match[4] });
    }

    lastIndex = pattern.lastIndex;
  }

  if (lastIndex < input.length) {
    tokens.push({ type: 'text', raw: input.slice(lastIndex) });
  }

  return tokens;
}

export function renderMarkdownMini(input: string): Node[] {
  const tokens = tokenize(input);
  return tokens.map((tok) => {
    if (tok.type === 'text') {
      return document.createTextNode(tok.raw);
    }
    if (tok.type === 'bold') {
      const el = document.createElement('strong');
      el.textContent = tok.inner;
      return el;
    }
    if (tok.type === 'italic') {
      const el = document.createElement('em');
      el.textContent = tok.inner;
      return el;
    }
    // link
    const el = document.createElement('a');
    el.textContent = tok.text;
    // Only allow http/https URLs to prevent javascript: injection
    const safeUrl = /^https?:\/\//i.test(tok.url) ? tok.url : '#';
    el.setAttribute('href', safeUrl);
    el.setAttribute('target', '_blank');
    el.setAttribute('rel', 'noopener noreferrer');
    return el;
  });
}

/** Render markdown into a container span — convenience wrapper. */
export function markdownSpan(input: string): HTMLSpanElement {
  const span = document.createElement('span');
  span.append(...renderMarkdownMini(input));
  return span;
}

export { escapeHtml };
